import { useState } from "react";

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct: number;
}

export interface QuizData {
  id: string;
  questions: QuizQuestion[];
  passing_score: number;
}

interface QuizRunnerProps {
  quiz: QuizData;
  onComplete?: (score: number, passed: boolean) => void;
  onClose?: () => void;
}

export function QuizRunner({ quiz, onComplete, onClose }: QuizRunnerProps) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const total = quiz.questions.length;
  const question = quiz.questions[currentIdx];

  function handleSelect(idx: number) {
    setSelected(idx);
  }

  function handleNext() {
    if (selected === null) return;
    setAnswers((prev) => ({ ...prev, [question.id]: selected }));
    setSelected(null);
    if (currentIdx < total - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      handleSubmit();
    }
  }

  function handleSubmit() {
    const finalAnswers = { ...answers, [question.id]: selected };
    let correct = 0;
    for (const q of quiz.questions) {
      if (finalAnswers[q.id] === q.correct) correct++;
    }
    const score = Math.round((correct / total) * 100);
    const passed = score >= quiz.passing_score;
    setSubmitted(true);
    onComplete?.(score, passed);
  }

  if (submitted) {
    let correct = 0;
    for (const q of quiz.questions) {
      if (answers[q.id] === q.correct) correct++;
    }
    const score = Math.round((correct / total) * 100);
    const passed = score >= quiz.passing_score;

    return (
      <div className="card" style={{ padding: "var(--space-5)", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
          <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>{passed ? "\u2705" : "\u26A0"}</div>
          <h3 style={{ fontSize: "1.25rem", marginBottom: "0.25rem" }}>{passed ? "Quiz Passed!" : "Quiz Not Passed"}</h3>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: passed ? "var(--accent-600)" : "var(--warning-600)" }}>
            {score}%
          </div>
          <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
            {correct} of {total} correct — Passing score: {quiz.passing_score}%
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "var(--space-4)" }}>
          {quiz.questions.map((q, qi) => {
            const userAnswer = answers[q.id];
            const isCorrect = userAnswer === q.correct;
            return (
              <div key={q.id} style={{ padding: "0.75rem", borderRadius: "var(--radius-md)", border: `1px solid ${isCorrect ? "var(--accent-300)" : "var(--error-300)"}`, background: isCorrect ? "var(--accent-50)" : "var(--error-50)" }}>
                <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>
                  {qi + 1}. {q.question}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  Your answer: {q.options[userAnswer] ?? "Not answered"}
                </div>
                {!isCorrect && (
                  <div style={{ fontSize: "0.75rem", color: "var(--accent-600)", fontWeight: 600, marginTop: "0.125rem" }}>
                    Correct: {q.options[q.correct]}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={onClose} className="btn btn-primary" style={{ width: "100%" }}>
          {passed ? "Continue to Next Module" : "Review and Retry"}
        </button>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: "var(--space-5)", maxWidth: "600px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
        <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-muted)" }}>
          Question {currentIdx + 1} of {total}
        </span>
        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Pass: {quiz.passing_score}%</span>
      </div>

      <div style={{ width: "100%", height: "4px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden", marginBottom: "var(--space-4)" }}>
        <div style={{ width: `${((currentIdx) / total) * 100}%`, height: "100%", background: "var(--accent-500)", borderRadius: "var(--radius-full)", transition: "width var(--transition)" }} />
      </div>

      <h3 style={{ fontSize: "1rem", marginBottom: "var(--space-3)" }}>{question.question}</h3>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
        {question.options.map((opt, idx) => (
          <button
            key={idx}
            onClick={() => handleSelect(idx)}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "var(--radius-md)",
              border: selected === idx ? "2px solid var(--accent-500)" : "1px solid var(--border)",
              background: selected === idx ? "var(--accent-50)" : "var(--surface)",
              cursor: "pointer",
              textAlign: "left",
              fontSize: "0.875rem",
              transition: "all var(--transition-fast)",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", background: selected === idx ? "var(--accent-500)" : "var(--neutral-200)", color: selected === idx ? "#fff" : "var(--text-muted)", fontSize: "0.75rem", fontWeight: 700, marginRight: "0.5rem" }}>
              {String.fromCharCode(65 + idx)}
            </span>
            {opt}
          </button>
        ))}
      </div>

      <button onClick={handleNext} disabled={selected === null} className="btn btn-primary" style={{ width: "100%" }}>
        {currentIdx < total - 1 ? "Next Question" : "Submit Quiz"}
      </button>
    </div>
  );
}
